import { useEffect, useState } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, WarningCircle, Sparkle, Scales, Calendar,
  Clock, Bank, ArrowSquareOut, Info,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * StatePack — Compliance dashboard.
 *
 * Left pane: current status scorecard.
 * Right pane: state picker + preview of the pack contents.
 * "Apply pack" wires the state's leave types, festival holidays, and payroll
 * rules into the tenant in one click.
 */
export default function StatePack() {
  const [packs, setPacks] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [detail, setDetail] = useState(null);
  const [applying, setApplying] = useState(false);
  const [replaceLeaves, setReplaceLeaves] = useState(false);
  const nav = useNavigate();

  const loadAll = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/compliance/state-packs"),
        api.get("/compliance/status"),
      ]);
      setPacks(p.data);
      setStatus(s.data);
      if (!selectedCode) setSelectedCode(s.data?.state_code || p.data[0]?.state_code || null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-line */ }, []);

  useEffect(() => {
    if (!selectedCode) return;
    api.get(`/compliance/state-pack/${selectedCode}`).then(r => setDetail(r.data)).catch(() => setDetail(null));
  }, [selectedCode]);

  const applyPack = async () => {
    if (!selectedCode) return;
    const msg = replaceLeaves
      ? `Apply ${selectedCode} pack and OVERWRITE all existing leave-type values with the state's statutory minimums?`
      : `Apply ${selectedCode} pack? Missing leave types will be added, existing ones kept as-is. Festival holidays will be added.`;
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const r = await api.post("/compliance/apply-state-pack", {
        state_code: selectedCode, replace_leaves: replaceLeaves, add_festivals: true,
      });
      toast.success(`Applied ${r.data.state_name} · ${r.data.leaves.created + r.data.leaves.updated} leaves · ${r.data.holidays.created} holidays added`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const scorePct = status?.total ? Math.round(100 * status.score / status.total) : 0;
  const scoreTone = scorePct >= 90 ? "emerald" : scorePct >= 60 ? "amber" : "rose";

  return (
    <AppShell title="State Compliance">

      {/* Scorecard */}
      <SectionCard
        title="Compliance scorecard"
        subtitle={status?.applied
          ? `Against ${status.state_name} S&E Act`
          : "No state pack applied yet — pick one below."}
        testid="compliance-scorecard"
      >
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className={`col-span-1 rounded-lg p-4 border
            ${scoreTone === "emerald" ? "bg-emerald-50 border-emerald-200"
              : scoreTone === "amber" ? "bg-amber-50 border-amber-200"
              : "bg-rose-50 border-rose-200"}`}>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Score</div>
            <div className={`text-3xl font-semibold mt-1
              ${scoreTone === "emerald" ? "text-emerald-700"
                : scoreTone === "amber" ? "text-amber-700"
                : "text-rose-700"}`}>{scorePct}<span className="text-lg">%</span></div>
            <div className="text-xs text-zinc-500 mt-1">
              {status?.score || 0} of {status?.total || 0} checks passing
            </div>
          </div>
          <div className="md:col-span-2 rounded-lg p-4 border border-zinc-200 bg-zinc-50">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Statute</div>
            <div className="text-sm text-zinc-800">{status?.act_reference || "—"}</div>
            <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
              <Info size={12}/> Values reflect FY 2026-27. Verify with your labour consultant.
            </div>
          </div>
        </div>

        <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="compliance-checks">
          {(status?.checks || []).map(c => (
            <div key={c.key} className="flex items-start gap-3 px-4 py-2.5">
              {c.ok
                ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0 mt-0.5"/>
                : <XCircle size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5"/>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{c.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{c.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* State Pack picker */}
      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <SectionCard title="Available state packs" subtitle="Pick your operating state" testid="state-packs-list">
          <div className="space-y-1.5">
            {packs.map(p => (
              <button key={p.state_code}
                onClick={() => setSelectedCode(p.state_code)}
                data-testid={`pack-${p.state_code}`}
                className={`w-full text-left p-3 rounded-md border transition-colors
                  ${selectedCode === p.state_code
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-zinc-200 hover:bg-zinc-50"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{p.state_name}</div>
                    <div className="text-xs text-zinc-500">{p.leave_count} leave types · {p.festival_count} festivals</div>
                  </div>
                  {status?.state_code === p.state_code && (
                    <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Applied</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard title={detail?.state_name || "…"}
          subtitle={detail?.act_reference || ""}
          testid="state-pack-detail"
          action={
            <Button size="sm" onClick={applyPack} disabled={applying || !selectedCode}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              data-testid="apply-pack-btn">
              <Sparkle size={14} weight="fill"/> {applying ? "Applying…" : "Apply pack"}
            </Button>
          }
        >
          {detail && (
            <div className="space-y-4 lg:col-span-2">
              {/* Leave types */}
              <div>
                <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500 uppercase tracking-wider">
                  <Scales size={14}/> Leave entitlements
                </div>
                <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100">
                  {detail.leave_types.map(lt => (
                    <div key={lt.code} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{lt.name} <span className="text-xs text-zinc-400 font-mono ml-1">({lt.code})</span></div>
                        <div className="text-xs text-zinc-500 mt-0.5">{lt.notes || "—"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold">{lt.default_days_per_year} days</div>
                        <div className="text-xs text-zinc-500">
                          {lt.accrual_cadence}{lt.carry_forward ? ` · carry ${lt.carry_forward_cap}` : ""}
                          {lt.encashable ? ` · encash ${lt.encashment_cap}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Festival holidays */}
              <div>
                <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500 uppercase tracking-wider">
                  <Calendar size={14}/> State festival holidays
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {detail.declared_festival_holidays.map(d => (
                    <Badge key={d} variant="outline" className="text-[11px] font-mono">{d}</Badge>
                  ))}
                </div>
              </div>

              {/* Rules */}
              <div>
                <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500 uppercase tracking-wider">
                  <Clock size={14}/> Working-time & pay rules
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <RuleRow label="Max daily hours" value={detail.max_daily_hours}/>
                  <RuleRow label="Max weekly hours" value={detail.max_weekly_hours}/>
                  <RuleRow label="Overtime multiplier" value={`${detail.overtime_multiplier}×`}/>
                  <RuleRow label="Work on holiday" value={`${detail.work_on_holiday_multiplier}× + ${detail.work_on_holiday_gives_comp_off ? "comp-off" : "no comp-off"}`}/>
                  <RuleRow label="EL encashment on exit" value={detail.el_encashment_on_exit ? "Yes" : "No"}/>
                  <RuleRow label="Weekly off" value={`${detail.weekly_off_days} day(s)/week`}/>
                  <RuleRow label="Min service for EL" value={`${detail.min_service_days_for_el} days`}/>
                  <RuleRow label="Form O leave register" value={detail.form_o_required ? "Required" : "Not required"}/>
                </div>
              </div>

              {/* Apply options */}
              <div className="border-t border-zinc-200 pt-3">
                <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={replaceLeaves}
                    onChange={(e) => setReplaceLeaves(e.target.checked)}
                    data-testid="replace-leaves"
                  />
                  Overwrite existing leave-type values (default: only fill missing)
                </label>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <div className="mt-4 flex gap-2">
        <Button size="sm" variant="outline" onClick={() => nav("/app/leave-admin")}>
          Manage leave types <ArrowSquareOut size={12}/>
        </Button>
        <Button size="sm" variant="outline" onClick={() => nav("/app/statutory-config")}>
          Statutory config <ArrowSquareOut size={12}/>
        </Button>
      </div>
    </AppShell>
  );
}

function RuleRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border border-zinc-200 rounded px-3 py-1.5">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className="font-mono text-sm">{value}</span>
    </div>
  );
}
